import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { ENTITY_CLASSIFICATIONS, EXCLUDED_PERSON_TYPES, ExcludedPersonType } from '@/constants/institution';
import type { InstitutionTaxSelfCertificationOrDefaults } from '@/features/institution/tax-self-certification/schema';

const InstitutionTaxSelfCertificationFieldsetClassificationLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('institutionDetails.taxSelfCertification.classification.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.taxSelfCertification.classification.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
InstitutionTaxSelfCertificationFieldsetClassificationLegend.displayName = 'InstitutionTaxSelfCertificationFieldsetClassificationLegend';

const InstitutionTaxSelfCertificationFieldsetClassification = () => {
  const form = useFormContext<InstitutionTaxSelfCertificationOrDefaults>();
  const { t } = useTranslation();
  const excludedPersonType = form.watch('excludedPersonType');

  return (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="excludedPersonType"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('institutionDetails.taxSelfCertification.classification.excludedPersonType')}*</FormLabel>
            <FormControl>
              <RadioGroup
                defaultValue={field.value}
                variant="cards"
                aria-label={t('institutionDetails.taxSelfCertification.classification.excludedPersonType')}
                onValueChange={field.onChange}
              >
                {EXCLUDED_PERSON_TYPES.map((type) => (
                  <FormItem key={type}>
                    <RadioGroupItemCard variant="outline">
                      <FormControl>
                        <RadioGroupItem value={type} />
                      </FormControl>
                      <FormLabel contained>{t(`institutionDetails.taxSelfCertification.classification.excludedPersonTypes.${type}`)}</FormLabel>
                    </RadioGroupItemCard>
                  </FormItem>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {excludedPersonType === ExcludedPersonType.RELATED_ENTITY_OF_LISTED_CORPORATION ? (
        <>
          <FormField
            control={form.control}
            name="listedEntityName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('institutionDetails.taxSelfCertification.classification.listedEntityName')}*</FormLabel>
                <FormControl>
                  <Input {...field} placeholder={t('institutionDetails.taxSelfCertification.placeholders.listedEntityName')} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="listedMarket"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('institutionDetails.taxSelfCertification.classification.listedMarket')}*</FormLabel>
                <FormControl>
                  <Input {...field} placeholder={t('institutionDetails.taxSelfCertification.placeholders.listedMarket')} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      ) : null}
      {excludedPersonType === ExcludedPersonType.NONE ? (
        <FormField
          control={form.control}
          name="entityClassification"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('institutionDetails.taxSelfCertification.classification.entityClassification')}*</FormLabel>
              <FormControl>
                <RadioGroup
                  defaultValue={field.value}
                  variant="cards"
                  aria-label={t('institutionDetails.taxSelfCertification.classification.entityClassification')}
                  onValueChange={field.onChange}
                >
                  {ENTITY_CLASSIFICATIONS.map((classification) => (
                    <FormItem key={classification}>
                      <RadioGroupItemCard variant="outline">
                        <FormControl>
                          <RadioGroupItem value={classification} />
                        </FormControl>
                        <FormLabel contained>{t(`institutionDetails.taxSelfCertification.classification.entityClassifications.${classification}`)}</FormLabel>
                      </RadioGroupItemCard>
                    </FormItem>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}
    </div>
  );
};
InstitutionTaxSelfCertificationFieldsetClassification.displayName = 'InstitutionTaxSelfCertificationFieldsetClassification';

export { InstitutionTaxSelfCertificationFieldsetClassification, InstitutionTaxSelfCertificationFieldsetClassificationLegend };
